import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getCertificate, createCertificate, updateCertificate,
} from '@/api/certificates';
import type {
  CertificateType, BaptismDetail, ConfirmationDetail,
  MarriageDetail, BurialDetail, CertificateFull,
} from '@/api/certificates';
import { listFamilies, getFamily } from '@/api/family';
import type { FamilyListItem, FamilyMember } from '@/api/family';
import { createFamilyMember } from '@/api/familyMember';
import { saveCemeteryRecord } from '@/api/cemetery';
import { createNonParishCemetery } from '@/api/nonParishCemetery';

type Source = 'parish' | 'outside';

type Mode = 'new' | 'edit' | 'print';

// ── Helpers ─────────────────────────────────────────────────────────────────

const TYPES: CertificateType[] = ['Baptism', 'Confirmation', 'Marriage', 'Burial'];

const fmtDate = (d?: string) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : '';

const Input = ({
  label, value, onChange, type = 'text', half = false,
}: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; half?: boolean;
}) => (
  <div className={half ? 'sm:col-span-1' : 'sm:col-span-2'}>
    <label className="block text-xs font-georgia text-gray-600 mb-0.5">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border rounded px-2 py-1.5 text-sm font-georgia focus:outline-none focus:ring-1 focus:ring-teal"
    />
  </div>
);

const Row = ({ label, value }: { label: string; value?: string }) => (
  <tr className="border-b border-gray-200">
    <td className="py-1 pr-3 text-xs font-georgia text-gray-500 w-40 align-top">{label}</td>
    <td className="py-1 text-sm font-georgia text-gray-800">{value || '—'}</td>
  </tr>
);

// ── Header / footer templates ──────────────────────────────────────────────
// Burial & Marriage use the Diocese of Chingleput register-extract style.
// Baptism & Confirmation use the Our Lady of Fatima Shrine certificate style.

function DioceseHeader({ registerLabel }: { registerLabel: string }) {
  return (
    <div className="text-center mb-3">
      <img
        src="/images/mary-fatima.png"
        alt="Our Lady of Fatima"
        className="h-16 mx-auto mb-1"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
      <p className="font-georgia text-base font-bold">Diocese of Chingleput</p>
      <p className="font-georgia text-sm uppercase tracking-wide mt-1">
        Extract from the Register of {registerLabel}
      </p>
      <div className="border-t border-gray-400 mt-3" />
    </div>
  );
}

function DioceseFooter() {
  return (
    <div className="mt-12 font-georgia text-sm">
      <p>I Certify that above extract is a true copy.</p>
      <div className="flex justify-between items-end mt-10 gap-6">
        <p className="whitespace-nowrap">Date.................</p>
        <p className="text-center whitespace-nowrap">(Parish Seal)</p>
        <div className="text-center">
          <div className="border-t border-gray-400 w-48 mb-1" />
          <p className="text-xs">Signature of PARISH PRIEST</p>
        </div>
      </div>
    </div>
  );
}

function ShrineHeader({ certLabel }: { certLabel: string }) {
  return (
    <div className="text-center mb-3">
      <img
        src="/images/mary-fatima.png"
        alt="Our Lady of Fatima"
        className="h-16 mx-auto mb-1"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
      <p className="font-georgia text-lg font-bold tracking-wide">OUR LADY OF FATIMA SHRINE</p>
      <p className="font-georgia text-xs text-gray-600 mt-0.5">
        Velachery Main Road, East Tambaram, Chennai - 600 059. Phone : 044-22390722
      </p>
      <div className="inline-block border border-gray-800 px-4 py-1 mt-3">
        <p className="font-georgia text-sm font-bold uppercase tracking-wide">{certLabel} Certificate</p>
      </div>
      <div className="border-t border-gray-400 mt-3" />
    </div>
  );
}

function ShrineFooter({ registerLabel }: { registerLabel: string }) {
  return (
    <div className="mt-12 font-georgia text-sm">
      <p>I Certify that the above is true copy of the entry in {registerLabel} register kept in this Shrine.</p>
      <div className="flex justify-between items-end mt-10 gap-6">
        <p className="whitespace-nowrap">Date :</p>
        <p className="text-center whitespace-nowrap">Seal</p>
        <div className="text-center">
          <div className="border-t border-gray-400 w-48 mb-1" />
          <p className="text-xs">Parish Priest</p>
        </div>
      </div>
    </div>
  );
}

// ── Baptism fields ────────────────────────────────────────────────────────────

function BaptismForm({ detail, onChange }: { detail: BaptismDetail; onChange: (d: BaptismDetail) => void }) {
  const s = (key: keyof BaptismDetail) => (v: string) => onChange({ ...detail, [key]: v });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Input label="Date of Baptism"   value={detail.dateOfBaptism   || ''} onChange={s('dateOfBaptism')}   type="date" half />
      <Input label="Date of Birth"     value={detail.dateOfBirth     || ''} onChange={s('dateOfBirth')}     type="date" half />
      <Input label="Place of Birth"    value={detail.placeOfBirth    || ''} onChange={s('placeOfBirth')} />
      <Input label="Name"              value={detail.name            || ''} onChange={s('name')}            half />
      <Input label="Sex"               value={detail.sex             || ''} onChange={s('sex')}             half />
      <Input label="Father's Name"     value={detail.fatherName      || ''} onChange={s('fatherName')}      half />
      <Input label="Mother's Name"     value={detail.motherName      || ''} onChange={s('motherName')}      half />
      <Input label="Residence"         value={detail.residence       || ''} onChange={s('residence')} />
      <Input label="Father's Profession" value={detail.fatherProfession || ''} onChange={s('fatherProfession')} />
      <Input label="God Parents"       value={detail.godParents      || ''} onChange={s('godParents')} />
      <Input label="Minister of Baptism" value={detail.ministerOfBaptism || ''} onChange={s('ministerOfBaptism')} />
      <Input label="Confirmed (when/where)" value={detail.confirmed  || ''} onChange={s('confirmed')} />
      <Input label="Married (when/where)"   value={detail.married    || ''} onChange={s('married')} />
      <Input label="Remarks"           value={detail.remarks         || ''} onChange={s('remarks')} />
    </div>
  );
}

function BaptismPrint({ d, master }: { d: BaptismDetail; master: Partial<CertificateFull> }) {
  return (
    <div className="space-y-3">
      <ShrineHeader certLabel="Baptism" />
      <table className="w-full mt-4">
        <tbody>
          <Row label="Serial No."         value={master.refSerialNo ?? undefined} />
          <Row label="Kept at"            value={master.keptAt ?? undefined} />
          <Row label="Date of Baptism"    value={fmtDate(d.dateOfBaptism)} />
          <Row label="Date of Birth"      value={fmtDate(d.dateOfBirth)} />
          <Row label="Place of Birth"     value={d.placeOfBirth} />
          <Row label="Name"               value={d.name} />
          <Row label="Sex"                value={d.sex} />
          <Row label="Father's Name"      value={d.fatherName} />
          <Row label="Mother's Name"      value={d.motherName} />
          <Row label="Residence"          value={d.residence} />
          <Row label="Father's Profession" value={d.fatherProfession} />
          <Row label="God Parents"        value={d.godParents} />
          <Row label="Minister"           value={d.ministerOfBaptism} />
          <Row label="Confirmed"          value={d.confirmed} />
          <Row label="Married"            value={d.married} />
          <Row label="Remarks"            value={d.remarks} />
        </tbody>
      </table>
      <ShrineFooter registerLabel="Baptism" />
    </div>
  );
}

// ── Confirmation fields ───────────────────────────────────────────────────────

function ConfirmationForm({ detail, onChange }: { detail: ConfirmationDetail; onChange: (d: ConfirmationDetail) => void }) {
  const s = (key: keyof ConfirmationDetail) => (v: string) => onChange({ ...detail, [key]: v });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Input label="Name and Surname"   value={String(detail.nameAndSurname || '')} onChange={s('nameAndSurname')} />
      <Input label="Age"                value={String(detail.age            || '')} onChange={s('age')}           half />
      <Input label="When Confirmed"     value={String(detail.whenConfirmed  || '')} onChange={s('whenConfirmed')}  type="date" half />
      <Input label="Where Confirmed"    value={String(detail.whereConfirmed || '')} onChange={s('whereConfirmed')} />
      <Input label="Parents' Names"     value={String(detail.parentsNames   || '')} onChange={s('parentsNames')} />
      <Input label="Sponsor Name"       value={String(detail.sponsorName    || '')} onChange={s('sponsorName')}    half />
      <Input label="Bishop's Name"      value={String(detail.bishopsName    || '')} onChange={s('bishopsName')}    half />
      <Input label="Remarks"            value={String(detail.remarks        || '')} onChange={s('remarks')} />
    </div>
  );
}

function ConfirmationPrint({ d, master }: { d: ConfirmationDetail; master: Partial<CertificateFull> }) {
  return (
    <div className="space-y-3">
      <ShrineHeader certLabel="Confirmation" />
      <table className="w-full mt-4">
        <tbody>
          <Row label="Serial No."       value={master.refSerialNo ?? undefined} />
          <Row label="Kept at"          value={master.keptAt ?? undefined} />
          <Row label="Name and Surname" value={String(d.nameAndSurname || '')} />
          <Row label="Age"              value={String(d.age || '')} />
          <Row label="When Confirmed"   value={fmtDate(String(d.whenConfirmed || ''))} />
          <Row label="Where Confirmed"  value={String(d.whereConfirmed || '')} />
          <Row label="Parents' Names"   value={String(d.parentsNames || '')} />
          <Row label="Sponsor"          value={String(d.sponsorName || '')} />
          <Row label="Bishop"           value={String(d.bishopsName || '')} />
          <Row label="Remarks"          value={String(d.remarks || '')} />
        </tbody>
      </table>
      <ShrineFooter registerLabel="Confirmation" />
    </div>
  );
}

// ── Marriage fields ───────────────────────────────────────────────────────────

function MarriageForm({ detail, onChange }: { detail: MarriageDetail; onChange: (d: MarriageDetail) => void }) {
  const s = (key: keyof MarriageDetail) => (v: string) => onChange({ ...detail, [key]: v });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input label="Date of Marriage"    value={String(detail.dateOfMarriage  || '')} onChange={s('dateOfMarriage')}  type="date" half />
        <Input label="Place of Marriage"   value={String(detail.placeOfMarriage || '')} onChange={s('placeOfMarriage')} half />
        <Input label="By Banns or Licence" value={String(detail.byBannsOrLicence|| '')} onChange={s('byBannsOrLicence')} half />
        <Input label="Witness"             value={String(detail.witness         || '')} onChange={s('witness')}          half />
        <Input label="Minister"            value={String(detail.minister        || '')} onChange={s('minister')} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Bridegroom column */}
        <div className="space-y-2">
          <p className="text-xs font-bold font-georgia text-navy uppercase tracking-wide">Bridegroom</p>
          <Input label="Name"        value={String(detail.bridegroomName       || '')} onChange={s('bridegroomName')} />
          <Input label="Age"         value={String(detail.bridegroomAge        || '')} onChange={s('bridegroomAge')} half />
          <Input label="Condition"   value={String(detail.bridegroomCondition  || '')} onChange={s('bridegroomCondition')} half />
          <Input label="Profession"  value={String(detail.bridegroomProfession || '')} onChange={s('bridegroomProfession')} />
          <Input label="Residence"   value={String(detail.bridegroomResidence  || '')} onChange={s('bridegroomResidence')} />
          <Input label="Parents"     value={String(detail.bridegroomParents    || '')} onChange={s('bridegroomParents')} />
        </div>
        {/* Bride column */}
        <div className="space-y-2">
          <p className="text-xs font-bold font-georgia text-navy uppercase tracking-wide">Bride</p>
          <Input label="Name"        value={String(detail.brideName       || '')} onChange={s('brideName')} />
          <Input label="Age"         value={String(detail.brideAge        || '')} onChange={s('brideAge')} half />
          <Input label="Condition"   value={String(detail.brideCondition  || '')} onChange={s('brideCondition')} half />
          <Input label="Profession"  value={String(detail.brideProfession || '')} onChange={s('brideProfession')} />
          <Input label="Residence"   value={String(detail.brideResidence  || '')} onChange={s('brideResidence')} />
          <Input label="Parents"     value={String(detail.brideParents    || '')} onChange={s('brideParents')} />
        </div>
      </div>
    </div>
  );
}

function MarriagePrint({ d, master }: { d: MarriageDetail; master: Partial<CertificateFull> }) {
  return (
    <div className="space-y-3">
      <DioceseHeader registerLabel="Marriages" />
      <table className="w-full mt-4">
        <tbody>
          <Row label="Serial No."        value={master.refSerialNo ?? undefined} />
          <Row label="Kept at"           value={master.keptAt ?? undefined} />
          <Row label="Date of Marriage"  value={fmtDate(String(d.dateOfMarriage || ''))} />
          <Row label="Place of Marriage" value={String(d.placeOfMarriage || '')} />
          <Row label="By Banns/Licence"  value={String(d.byBannsOrLicence || '')} />
        </tbody>
      </table>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3 print:grid-cols-2">
        <table>
          <thead><tr><th className="text-left text-xs font-georgia text-gray-500 pb-1">Bridegroom</th></tr></thead>
          <tbody>
            <Row label="Name"       value={String(d.bridegroomName || '')} />
            <Row label="Age"        value={String(d.bridegroomAge || '')} />
            <Row label="Condition"  value={String(d.bridegroomCondition || '')} />
            <Row label="Profession" value={String(d.bridegroomProfession || '')} />
            <Row label="Residence"  value={String(d.bridegroomResidence || '')} />
            <Row label="Parents"    value={String(d.bridegroomParents || '')} />
          </tbody>
        </table>
        <table>
          <thead><tr><th className="text-left text-xs font-georgia text-gray-500 pb-1">Bride</th></tr></thead>
          <tbody>
            <Row label="Name"       value={String(d.brideName || '')} />
            <Row label="Age"        value={String(d.brideAge || '')} />
            <Row label="Condition"  value={String(d.brideCondition || '')} />
            <Row label="Profession" value={String(d.brideProfession || '')} />
            <Row label="Residence"  value={String(d.brideResidence || '')} />
            <Row label="Parents"    value={String(d.brideParents || '')} />
          </tbody>
        </table>
      </div>
      <table className="w-full">
        <tbody>
          <Row label="Witness"   value={String(d.witness || '')} />
          <Row label="Minister"  value={String(d.minister || '')} />
        </tbody>
      </table>
      <DioceseFooter />
    </div>
  );
}

// ── Burial fields ─────────────────────────────────────────────────────────────

function BurialForm({ detail, onChange }: { detail: BurialDetail; onChange: (d: BurialDetail) => void }) {
  const s = (key: keyof BurialDetail) => (v: string) => onChange({ ...detail, [key]: v });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Input label="Name and Surname"   value={String(detail.nameAndSurname || '')} onChange={s('nameAndSurname')} />
      <Input label="Sex"                value={String(detail.sex            || '')} onChange={s('sex')}            half />
      <Input label="Age"                value={String(detail.age            || '')} onChange={s('age')}            half />
      <Input label="When Died"          value={String(detail.whenDied       || '')} onChange={s('whenDied')}       type="date" half />
      <Input label="Parents or Spouse"  value={String(detail.parentsOrSpouse|| '')} onChange={s('parentsOrSpouse')} />
      <Input label="Trade / Profession" value={String(detail.tradeProfession|| '')} onChange={s('tradeProfession')} half />
      <Input label="Cause of Death"     value={String(detail.causeOfDeath   || '')} onChange={s('causeOfDeath')}   half />
      <Input label="When Buried"        value={String(detail.whenBuried     || '')} onChange={s('whenBuried')}     type="date" half />
      <Input label="Where Buried"       value={String(detail.whereBuried    || '')} onChange={s('whereBuried')}    half />
      <Input label="Last Sacraments"    value={String(detail.lastSacraments || '')} onChange={s('lastSacraments')} />
      <Input label="Conf / Viaticum / Anointing" value={String(detail.confesViaticAnointing || '')} onChange={s('confesViaticAnointing')} />
      <Input label="Minister of Last Sacraments" value={String(detail.ministerOfLastSacraments || '')} onChange={s('ministerOfLastSacraments')} />
      <Input label="Buried By"          value={String(detail.buriedBy       || '')} onChange={s('buriedBy')} />
      <Input label="Remarks"            value={String(detail.remarks        || '')} onChange={s('remarks')} />
    </div>
  );
}

function BurialPrint({ d, master }: { d: BurialDetail; master: Partial<CertificateFull> }) {
  return (
    <div className="space-y-3">
      <DioceseHeader registerLabel="Burials" />
      <table className="w-full mt-4">
        <tbody>
          <Row label="Serial No."     value={master.refSerialNo ?? undefined} />
          <Row label="Kept at"        value={master.keptAt ?? undefined} />
          <Row label="Name and Surname" value={String(d.nameAndSurname || '')} />
          <Row label="Sex"            value={String(d.sex || '')} />
          <Row label="Age"            value={String(d.age || '')} />
          <Row label="When Died"      value={fmtDate(String(d.whenDied || ''))} />
          <Row label="Parents/Spouse" value={String(d.parentsOrSpouse || '')} />
          <Row label="Trade/Profession" value={String(d.tradeProfession || '')} />
          <Row label="Cause of Death" value={String(d.causeOfDeath || '')} />
          <Row label="When Buried"    value={fmtDate(String(d.whenBuried || ''))} />
          <Row label="Where Buried"   value={String(d.whereBuried || '')} />
          <Row label="Last Sacraments" value={String(d.lastSacraments || '')} />
          <Row label="Conf/Viat/Anoint" value={String(d.confesViaticAnointing || '')} />
          <Row label="Minister"       value={String(d.ministerOfLastSacraments || '')} />
          <Row label="Buried By"      value={String(d.buriedBy || '')} />
          <Row label="Remarks"        value={String(d.remarks || '')} />
        </tbody>
      </table>
      <DioceseFooter />
    </div>
  );
}

// ── Parish family / outside-parish person picker ───────────────────────────────
// Reusable across Baptism, Confirmation, Marriage (per side) and Burial.

// sp_GetFamilyMembersDetailsByFamilyId's column casing isn't guaranteed
// (camelCase / PascalCase / snake_case all seen elsewhere — see FamilyDetailPage's
// `pick` helper). Without this, m.memberId is undefined for every row, so the
// <select> can never actually select anyone and the name-autofill lookups never match.
const pickVal = (row: Record<string, unknown>, keys: string[]): string | undefined => {
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null && v !== '') return String(v);
  }
  return undefined;
};

const normalizeMember = (m: FamilyMember): FamilyMember => {
  const row = m as unknown as Record<string, unknown>;
  const idRaw = pickVal(row, ['memberId', 'member_id', 'MemberId', 'MemberID']);
  return {
    ...m,
    memberId:     idRaw !== undefined ? Number(idRaw) : undefined,
    name:         pickVal(row, ['name', 'Name']) ?? m.name,
    relationship: pickVal(row, ['relationship', 'Relationship']) ?? m.relationship,
    gender:       pickVal(row, ['gender', 'Gender']) ?? m.gender,
  };
};

function PersonSourceSelector({
  source, setSource,
  familyId, setFamilyId,
  memberId, setMemberId,
  members, setMembers,
  memberLabel = 'Member',
  allowNewMember = false,
  newMemberSelected = false,
  setNewMemberSelected,
}: {
  source: Source;
  setSource: (s: Source) => void;
  familyId: number | null;
  setFamilyId: (id: number | null) => void;
  memberId: number | null;
  setMemberId: (id: number | null) => void;
  members: FamilyMember[];
  setMembers: (m: FamilyMember[]) => void;
  memberLabel?: string;
  allowNewMember?: boolean;
  newMemberSelected?: boolean;
  setNewMemberSelected?: (v: boolean) => void;
}) {
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState<FamilyListItem[]>([]);
  const [familyLabel, setLabel] = useState('');

  useEffect(() => {
    if (source !== 'parish' || familyId || !query.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      listFamilies({ head: query, pageSize: 8 })
        .then((r) => setResults(r.items))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [query, source, familyId]);

  const pickFamily = async (f: FamilyListItem) => {
    setFamilyId(f.familyId);
    setLabel(`${f.familyCode} — ${f.familyHead || 'Unnamed'}`);
    setQuery(''); setResults([]);
    setMemberId(null);
    setNewMemberSelected?.(false);
    try {
      const detail = await getFamily(f.familyId);
      setMembers((detail.members || []).map(normalizeMember));
    } catch {
      setMembers([]);
    }
  };

  const clearFamily = () => {
    setFamilyId(null); setMemberId(null); setMembers([]); setLabel('');
    setNewMemberSelected?.(false);
  };

  return (
    <div className="border rounded p-3 space-y-2 bg-offwhite">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setSource('parish')}
          className={`flex-1 px-2 py-1 text-xs rounded font-georgia transition-colors ${
            source === 'parish' ? 'bg-teal text-white' : 'bg-white border text-gray-600'
          }`}
        >
          Parish family
        </button>
        <button
          type="button"
          onClick={() => { setSource('outside'); clearFamily(); }}
          className={`flex-1 px-2 py-1 text-xs rounded font-georgia transition-colors ${
            source === 'outside' ? 'bg-teal text-white' : 'bg-white border text-gray-600'
          }`}
        >
          Outside parish
        </button>
      </div>

      {source === 'parish' && (
        <div className="space-y-2">
          {!familyId && (
            <div className="relative">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search family by head of family's name…"
                className="w-full border rounded px-2 py-1.5 text-sm font-georgia focus:outline-none focus:ring-1 focus:ring-teal"
              />
              {results.length > 0 && (
                <div className="absolute z-10 bg-white border rounded shadow-card w-full mt-1 max-h-48 overflow-y-auto">
                  {results.map((f) => (
                    <button
                      key={f.familyId}
                      type="button"
                      onClick={() => pickFamily(f)}
                      className="block w-full text-left px-2 py-1.5 text-xs hover:bg-offwhite font-georgia"
                    >
                      {f.familyCode} — {f.familyHead || 'Unnamed'}{f.anbiyamName ? ` (${f.anbiyamName})` : ''}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {familyId && (
            <div className="flex items-center justify-between text-xs font-georgia bg-white border rounded px-2 py-1.5">
              <span className="text-navy">{familyLabel || `Family #${familyId}`}</span>
              <button type="button" onClick={clearFamily} className="text-teal hover:underline">Change</button>
            </div>
          )}
          {familyId && (
            <select
              value={newMemberSelected ? 'new' : (memberId === null ? '' : String(memberId))}
              onChange={(e) => {
                if (e.target.value === 'new') { setNewMemberSelected?.(true); setMemberId(null); }
                else { setNewMemberSelected?.(false); setMemberId(e.target.value ? Number(e.target.value) : null); }
              }}
              className="w-full border rounded px-2 py-1.5 text-sm font-georgia focus:outline-none focus:ring-1 focus:ring-teal"
            >
              <option value="">Select {memberLabel.toLowerCase()}…</option>
              {members.map((m) => (
                <option key={m.memberId} value={m.memberId}>{m.name} ({m.relationship})</option>
              ))}
              {allowNewMember && <option value="new">+ New member (not yet added to family)</option>}
            </select>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const EMPTY: Record<CertificateType, object> = {
  Baptism:      {} as BaptismDetail,
  Confirmation: {} as ConfirmationDetail,
  Marriage:     {} as MarriageDetail,
  Burial:       {} as BurialDetail,
};

export default function CertificateFormPage({ mode: modeProp }: { mode?: Mode }) {
  const navigate        = useNavigate();
  const { id }          = useParams<{ id?: string }>();
  const isPrint = modeProp === 'print';
  const isEdit  = !!id && modeProp !== 'print';

  const [certType,    setCertType]  = useState<CertificateType>('Baptism');
  const [refSerialNo, setRefNo]     = useState('');
  const [keptAt,      setKeptAt]    = useState('');
  const [issuedDate,  setIssued]    = useState('');
  const [detail,      setDetail]    = useState<object>(EMPTY['Baptism']);
  const [loading,     setLoading]   = useState(false);
  const [saving,      setSaving]    = useState(false);
  const [error,       setError]     = useState('');
  const [master,      setMaster]    = useState<Partial<CertificateFull>>({});

  // Baptism — parents picked from a parish family; child becomes a new member
  const [baptSource,   setBaptSource]   = useState<Source>('outside');
  const [baptFamilyId, setBaptFamilyId] = useState<number | null>(null);
  const [baptMembers,  setBaptMembers]  = useState<FamilyMember[]>([]);
  const [baptFatherId, setBaptFatherId] = useState<number | null>(null);
  const [baptMotherId, setBaptMotherId] = useState<number | null>(null);

  // Confirmation — the person being confirmed
  const [confSource,   setConfSource]   = useState<Source>('outside');
  const [confFamilyId, setConfFamilyId] = useState<number | null>(null);
  const [confMembers,  setConfMembers]  = useState<FamilyMember[]>([]);
  const [confMemberId, setConfMemberId] = useState<number | null>(null);

  // Marriage — groom and bride sides are fully independent
  const [groomSource,   setGroomSource]   = useState<Source>('outside');
  const [groomFamilyId, setGroomFamilyId] = useState<number | null>(null);
  const [groomMembers,  setGroomMembers]  = useState<FamilyMember[]>([]);
  const [groomMemberId, setGroomMemberId] = useState<number | null>(null);
  const [brideSource,   setBrideSource]   = useState<Source>('outside');
  const [brideFamilyId, setBrideFamilyId] = useState<number | null>(null);
  const [brideMembers,  setBrideMembers]  = useState<FamilyMember[]>([]);
  const [brideMemberId, setBrideMemberId] = useState<number | null>(null);

  // Burial — existing parish member / new member of a parish family / outside parish
  const [burialSource,    setBurialSource]    = useState<Source>('outside');
  const [burialFamilyId,  setBurialFamilyId]  = useState<number | null>(null);
  const [burialMembers,   setBurialMembers]   = useState<FamilyMember[]>([]);
  const [burialMemberId,  setBurialMemberId]  = useState<number | null>(null);
  const [burialNewMember, setBurialNewMember] = useState(false);

  const resetLinkage = () => {
    setBaptSource('outside'); setBaptFamilyId(null); setBaptMembers([]); setBaptFatherId(null); setBaptMotherId(null);
    setConfSource('outside'); setConfFamilyId(null); setConfMembers([]); setConfMemberId(null);
    setGroomSource('outside'); setGroomFamilyId(null); setGroomMembers([]); setGroomMemberId(null);
    setBrideSource('outside'); setBrideFamilyId(null); setBrideMembers([]); setBrideMemberId(null);
    setBurialSource('outside'); setBurialFamilyId(null); setBurialMembers([]); setBurialMemberId(null); setBurialNewMember(false);
  };

  // Load existing record when editing or printing
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getCertificate(Number(id))
      .then((rec) => {
        setCertType(rec.certificateType);
        setRefNo(rec.refSerialNo || '');
        setKeptAt(rec.keptAt || '');
        setIssued(rec.issuedDate?.slice(0, 10) || '');
        setDetail(rec.detail || EMPTY[rec.certificateType]);
        setMaster(rec);
      })
      .catch(() => setError('Failed to load certificate.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleTypeChange = (t: CertificateType) => {
    setCertType(t);
    setDetail(EMPTY[t]);
    resetLinkage();
  };

  // Auto-fill name fields when a parish member is picked
  useEffect(() => {
    if (baptSource !== 'parish' || baptFatherId === null) return;
    const m = baptMembers.find((x) => x.memberId === baptFatherId);
    if (m) setDetail((d) => ({ ...(d as BaptismDetail), fatherName: m.name }));
  }, [baptFatherId, baptMembers, baptSource]);

  useEffect(() => {
    if (baptSource !== 'parish' || baptMotherId === null) return;
    const m = baptMembers.find((x) => x.memberId === baptMotherId);
    if (m) setDetail((d) => ({ ...(d as BaptismDetail), motherName: m.name }));
  }, [baptMotherId, baptMembers, baptSource]);

  useEffect(() => {
    if (confSource !== 'parish' || confMemberId === null) return;
    const m = confMembers.find((x) => x.memberId === confMemberId);
    if (m) setDetail((d) => ({ ...(d as ConfirmationDetail), nameAndSurname: m.name }));
  }, [confMemberId, confMembers, confSource]);

  useEffect(() => {
    if (groomSource !== 'parish' || groomMemberId === null) return;
    const m = groomMembers.find((x) => x.memberId === groomMemberId);
    if (m) setDetail((d) => ({ ...(d as MarriageDetail), bridegroomName: m.name }));
  }, [groomMemberId, groomMembers, groomSource]);

  useEffect(() => {
    if (brideSource !== 'parish' || brideMemberId === null) return;
    const m = brideMembers.find((x) => x.memberId === brideMemberId);
    if (m) setDetail((d) => ({ ...(d as MarriageDetail), brideName: m.name }));
  }, [brideMemberId, brideMembers, brideSource]);

  useEffect(() => {
    if (burialSource === 'outside' || burialMemberId === null) return;
    const m = burialMembers.find((x) => x.memberId === burialMemberId);
    if (m) setDetail((d) => ({ ...(d as BurialDetail), nameAndSurname: m.name, sex: m.gender || (d as BurialDetail).sex }));
  }, [burialMemberId, burialMembers, burialSource]);

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      let workingDetail: object = detail;
      let familyId: number | undefined;
      let familyMemberId: number | undefined;

      if (certType === 'Baptism' && baptSource === 'parish' && baptFamilyId) {
        const d = workingDetail as BaptismDetail;
        const childMember = await createFamilyMember(baptFamilyId, {
          name:         d.name || 'Unnamed',
          relationship: 'Child',
          gender:       d.sex,
          dob:          d.dateOfBirth || null,
          baptizedDate: d.dateOfBaptism || null,
        });
        familyId = baptFamilyId;
        familyMemberId = (childMember as { memberId?: number })?.memberId;
      }

      if (certType === 'Confirmation' && confSource === 'parish' && confFamilyId && confMemberId) {
        familyId = confFamilyId;
        familyMemberId = confMemberId;
      }

      if (certType === 'Marriage') {
        workingDetail = {
          ...(workingDetail as MarriageDetail),
          groomFamilyId:       groomSource === 'parish' ? groomFamilyId ?? undefined : undefined,
          groomFamilyMemberId: groomSource === 'parish' ? groomMemberId ?? undefined : undefined,
          brideFamilyId:       brideSource === 'parish' ? brideFamilyId ?? undefined : undefined,
          brideFamilyMemberId: brideSource === 'parish' ? brideMemberId ?? undefined : undefined,
        };
        if (groomSource === 'parish' && groomFamilyId) {
          familyId = groomFamilyId;
          familyMemberId = groomMemberId ?? undefined;
        }
      }

      if (certType === 'Burial') {
        const d = workingDetail as BurialDetail;
        if (burialSource === 'parish' && burialFamilyId) {
          let memberId = burialMemberId;
          if (burialNewMember) {
            const newMember = await createFamilyMember(burialFamilyId, {
              name:         d.nameAndSurname || 'Unnamed',
              relationship: 'Other',
              gender:       d.sex,
            });
            memberId = (newMember as { memberId?: number })?.memberId ?? null;
          }
          if (memberId) {
            familyId = burialFamilyId;
            familyMemberId = memberId;
            await saveCemeteryRecord({
              familyId,
              memberId,
              burialPlace:  d.whereBuried,
              dateOfDeath:  d.whenDied || null,
              dateOfBurial: d.whenBuried || null,
              notes:        d.remarks,
            });
          }
        } else if (burialSource === 'outside') {
          const rec = await createNonParishCemetery({
            name:         d.nameAndSurname || 'Unnamed',
            gender:       d.sex,
            dateOfDeath:  d.whenDied || null,
            dateOfBurial: d.whenBuried || null,
            notes:        d.remarks,
          });
          workingDetail = { ...d, nonParishCemeteryId: rec.nonparish_cemetery_id };
        }
      }

      const payload = {
        type: certType, refSerialNo, keptAt, issuedDate,
        detail: workingDetail,
        familyId, familyMemberId,
      };
      if (isEdit) {
        await updateCertificate(Number(id), payload);
      } else {
        const res = await createCertificate({ ...payload, type: certType });
        navigate(`/certificates/${res.certificateId}/print`, { replace: true });
        return;
      }
      navigate('/certificates');
    } catch {
      setError('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Print mode ──────────────────────────────────────────────────────────────
  if (isPrint) {
    if (loading) return <p className="p-8 text-center text-gray-400">Loading…</p>;
    return (
      <div className="p-4 md:p-8">
        {/* Print toolbar — hidden on actual print */}
        <div className="print:hidden flex flex-wrap gap-3 mb-6">
          <button
            onClick={() => window.print()}
            className="px-4 py-2 bg-teal text-white text-sm font-georgia rounded hover:bg-teal-700"
          >
            🖨 Print / Save PDF
          </button>
          <button
            onClick={() => navigate(`/certificates/${id}/edit`)}
            className="px-4 py-2 border text-sm font-georgia rounded hover:bg-gray-50"
          >
            ✏ Edit
          </button>
          <button
            onClick={() => navigate('/certificates')}
            className="px-4 py-2 border text-sm font-georgia rounded hover:bg-gray-50"
          >
            ← Back to List
          </button>
        </div>

        {/* Certificate print area */}
        <div className="max-w-2xl mx-auto border border-gray-300 rounded p-4 sm:p-8 bg-gradient-to-b from-green-50 to-emerald-50 print:bg-white print:border-none print:shadow-none print:max-w-none print:p-8">
          {certType === 'Baptism'      && <BaptismPrint      d={detail as BaptismDetail}      master={master} />}
          {certType === 'Confirmation' && <ConfirmationPrint d={detail as ConfirmationDetail} master={master} />}
          {certType === 'Marriage'     && <MarriagePrint     d={detail as MarriageDetail}     master={master} />}
          {certType === 'Burial'       && <BurialPrint       d={detail as BurialDetail}       master={master} />}
        </div>
      </div>
    );
  }

  // ── Form mode ───────────────────────────────────────────────────────────────
  if (loading) return <p className="p-8 text-center text-gray-400">Loading…</p>;

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/certificates')} className="text-gray-400 hover:text-navy text-lg">←</button>
        <h1 className="text-xl font-georgia font-bold text-navy">
          {isEdit ? 'Edit Certificate' : 'New Certificate'}
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700 font-georgia">
          {error}
        </div>
      )}

      {/* Certificate type (only for new) */}
      {!isEdit && (
        <div>
          <p className="text-xs font-georgia text-gray-600 mb-2 font-bold">Certificate Type</p>
          <div className="flex gap-2 flex-wrap">
            {TYPES.map((t) => (
              <button
                key={t}
                onClick={() => handleTypeChange(t)}
                className={`px-3 py-1.5 rounded border text-sm font-georgia transition-colors ${
                  certType === t
                    ? 'bg-navy text-white border-navy'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Common header fields */}
      <div className="bg-white rounded-lg border p-4 space-y-3">
        <p className="text-xs font-bold font-georgia text-gray-400 uppercase tracking-wide">Common Details</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-georgia text-gray-600 mb-0.5">Serial / Ref No.</label>
            <input
              type="text"
              value={refSerialNo}
              onChange={(e) => setRefNo(e.target.value)}
              className="w-full border rounded px-2 py-1.5 text-sm font-georgia focus:outline-none focus:ring-1 focus:ring-teal"
            />
          </div>
          <div>
            <label className="block text-xs font-georgia text-gray-600 mb-0.5">Issued Date</label>
            <input
              type="date"
              value={issuedDate}
              onChange={(e) => setIssued(e.target.value)}
              className="w-full border rounded px-2 py-1.5 text-sm font-georgia focus:outline-none focus:ring-1 focus:ring-teal"
            />
          </div>
          <div>
            <label className="block text-xs font-georgia text-gray-600 mb-0.5">Kept At</label>
            <input
              type="text"
              value={keptAt}
              onChange={(e) => setKeptAt(e.target.value)}
              placeholder="Register / Book ref"
              className="w-full border rounded px-2 py-1.5 text-sm font-georgia focus:outline-none focus:ring-1 focus:ring-teal"
            />
          </div>
        </div>
      </div>

      {/* Type-specific fields */}
      <div className="bg-white rounded-lg border p-4 space-y-3">
        <p className="text-xs font-bold font-georgia text-gray-400 uppercase tracking-wide">
          {certType} Details
        </p>
        {certType === 'Baptism' && (
          <>
            <p className="text-xs font-georgia text-gray-500">Father — parish or outside parish</p>
            <PersonSourceSelector
              source={baptSource} setSource={setBaptSource}
              familyId={baptFamilyId} setFamilyId={setBaptFamilyId}
              memberId={baptFatherId} setMemberId={setBaptFatherId}
              members={baptMembers} setMembers={setBaptMembers}
              memberLabel="father"
            />
            {baptSource === 'parish' && baptFamilyId && (
              <select
                value={baptMotherId === null ? '' : String(baptMotherId)}
                onChange={(e) => setBaptMotherId(e.target.value ? Number(e.target.value) : null)}
                className="w-full border rounded px-2 py-1.5 text-sm font-georgia focus:outline-none focus:ring-1 focus:ring-teal"
              >
                <option value="">Select mother…</option>
                {baptMembers.map((m) => (
                  <option key={m.memberId} value={m.memberId}>{m.name} ({m.relationship})</option>
                ))}
              </select>
            )}
            {baptSource === 'parish' && baptFamilyId && (
              <p className="text-xs font-georgia text-teal bg-teal/10 rounded px-2 py-1.5">
                Saving will add the baptized child as a new member of this family.
              </p>
            )}
            <BaptismForm detail={detail as BaptismDetail} onChange={(d) => setDetail(d)} />
          </>
        )}
        {certType === 'Confirmation' && (
          <>
            <p className="text-xs font-georgia text-gray-500">Person being confirmed — parish or outside parish</p>
            <PersonSourceSelector
              source={confSource} setSource={setConfSource}
              familyId={confFamilyId} setFamilyId={setConfFamilyId}
              memberId={confMemberId} setMemberId={setConfMemberId}
              members={confMembers} setMembers={setConfMembers}
            />
            <ConfirmationForm detail={detail as ConfirmationDetail} onChange={(d) => setDetail(d)} />
          </>
        )}
        {certType === 'Marriage' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <p className="text-xs font-georgia text-gray-500">Bridegroom — parish or outside parish</p>
                <PersonSourceSelector
                  source={groomSource} setSource={setGroomSource}
                  familyId={groomFamilyId} setFamilyId={setGroomFamilyId}
                  memberId={groomMemberId} setMemberId={setGroomMemberId}
                  members={groomMembers} setMembers={setGroomMembers}
                />
              </div>
              <div className="space-y-1">
                <p className="text-xs font-georgia text-gray-500">Bride — parish or outside parish</p>
                <PersonSourceSelector
                  source={brideSource} setSource={setBrideSource}
                  familyId={brideFamilyId} setFamilyId={setBrideFamilyId}
                  memberId={brideMemberId} setMemberId={setBrideMemberId}
                  members={brideMembers} setMembers={setBrideMembers}
                />
              </div>
            </div>
            <MarriageForm detail={detail as MarriageDetail} onChange={(d) => setDetail(d)} />
          </>
        )}
        {certType === 'Burial' && (
          <>
            <p className="text-xs font-georgia text-gray-500">
              Existing parish member, a new member of a parish family, or outside parish
            </p>
            <PersonSourceSelector
              source={burialSource} setSource={setBurialSource}
              familyId={burialFamilyId} setFamilyId={setBurialFamilyId}
              memberId={burialMemberId} setMemberId={setBurialMemberId}
              members={burialMembers} setMembers={setBurialMembers}
              allowNewMember
              newMemberSelected={burialNewMember}
              setNewMemberSelected={setBurialNewMember}
            />
            {burialSource === 'parish' && burialFamilyId && (
              <p className="text-xs font-georgia text-teal bg-teal/10 rounded px-2 py-1.5">
                Saving will automatically create a matching cemetery record for this member.
              </p>
            )}
            {burialSource === 'outside' && (
              <p className="text-xs font-georgia text-teal bg-teal/10 rounded px-2 py-1.5">
                Saving will automatically create a non-parish cemetery record.
              </p>
            )}
            <BurialForm detail={detail as BurialDetail} onChange={(d) => setDetail(d)} />
          </>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 justify-end">
        <button
          onClick={() => navigate('/certificates')}
          className="px-4 py-2 border text-sm font-georgia rounded hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2 bg-teal text-white text-sm font-georgia rounded hover:bg-teal-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Save & Preview'}
        </button>
      </div>
    </div>
  );
}
